// Types for the events-based session rendering pipeline.
// These define the intermediate data model between raw SessionEvent[] from the API
// and the React rendering components. The ConversationModel is structured around
// turns (user→assistant exchange cycles) with items in event arrival order.
//
// See also: session-event.generated.ts for the raw API types.

import type {components} from './session-event.generated'
import type {NormalizedPrompt} from '../components/session-logs/prompt-log-utils'

// Re-export the generated types we use, with shorter aliases
export type SessionEvent = components['schemas']['SessionEvent']
export type SessionStartEvent = components['schemas']['SessionStartEvent']
export type SessionModelChangeEvent = components['schemas']['SessionModelChangeEvent']
export type SessionRemoteSteerableChangedEvent = components['schemas']['SessionRemoteSteerableChangedEvent']
export type UserMessageEvent = components['schemas']['UserMessageEvent']
export type AssistantMessageEvent = components['schemas']['AssistantMessageEvent']
export type AssistantTurnStartEvent = components['schemas']['AssistantTurnStartEvent']
export type AssistantTurnEndEvent = components['schemas']['AssistantTurnEndEvent']
export type AssistantIntentEvent = components['schemas']['AssistantIntentEvent']
export type AbortEvent = components['schemas']['AbortEvent']
export type ToolExecutionStartEvent = components['schemas']['ToolExecutionStartEvent']
export type ToolExecutionCompleteEvent = components['schemas']['ToolExecutionCompleteEvent']
export type ToolUserRequestedEvent = components['schemas']['ToolUserRequestedEvent']
export type CustomAgentStartedEvent = components['schemas']['CustomAgentStartedEvent']
export type CustomAgentCompletedEvent = components['schemas']['CustomAgentCompletedEvent']
export type CustomAgentFailedEvent = components['schemas']['CustomAgentFailedEvent']
export type PermissionRequestedEvent = components['schemas']['PermissionRequestedEvent']
export type PermissionCompletedEvent = components['schemas']['PermissionCompletedEvent']
export type ElicitationRequestedEvent = components['schemas']['ElicitationRequestedEvent']
export type ElicitationCompletedEvent = components['schemas']['ElicitationCompletedEvent']
export type ExitPlanModeRequestedEvent = components['schemas']['ExitPlanModeRequestedEvent']
export type ExitPlanModeCompletedEvent = components['schemas']['ExitPlanModeCompletedEvent']
export type HookStartEvent = components['schemas']['HookStartEvent']
export type HookEndEvent = components['schemas']['HookEndEvent']
export type SystemMessageEvent = components['schemas']['SystemMessageEvent']
export type UserInputRequestedEvent = components['schemas']['UserInputRequestedEvent']
export type UserInputCompletedEvent = components['schemas']['UserInputCompletedEvent']
export type ToolRequest = components['schemas']['ToolRequest']
export type BaseSessionEvent = components['schemas']['BaseSessionEvent']

// ─── Paired Event Structures ─────────────────────────────────────────
//
// Events that come in request/response pairs. The "response" field is null
// while the request is pending, and is set when the completion event arrives.

export interface PairedToolCall {
  toolCallId: string
  toolName: string
  arguments: unknown
  startEvent: ToolExecutionStartEvent | null
  completeEvent: ToolExecutionCompleteEvent | null
  parentToolCallId: string | null
}

// ─── Turn Items ──────────────────────────────────────────────────────
//
// Items within a turn, ordered by event arrival time.
// Tool completions and prompt responses UPDATE existing items in place —
// they do not create new items.

export interface AssistantTextItem {
  kind: 'assistant_text'
  id: string
  timestamp: string
  messageId: string
  content: string
  reasoningText: string | null
}

export interface ToolCallItem {
  kind: 'tool_call'
  id: string
  timestamp: string
  toolCall: PairedToolCall
}

// ─── Interactive Prompt Items ────────────────────────────────────────
//
// Each prompt type has its own item type with fully typed data.
// Completion events are set in-place when the response arrives.

export interface AskUserItem {
  kind: 'ask_user'
  id: string
  timestamp: string
  toolCallId: string
  question: string
  choices: string[] | null
  allowFreeform: boolean
  completedEvent: UserInputCompletedEvent | null
}

export interface PermissionItem {
  kind: 'permission'
  id: string
  timestamp: string
  requestId: string
  requestEvent: PermissionRequestedEvent
  completedEvent: PermissionCompletedEvent | null
}

export interface ElicitationItem {
  kind: 'elicitation'
  id: string
  timestamp: string
  requestId: string
  message: string
  schema: Record<string, unknown>
  toolCallId: string
  completedEvent: ElicitationCompletedEvent | null
}

export interface PlanApprovalItem {
  kind: 'plan_approval'
  id: string
  timestamp: string
  requestId: string
  toolCallId: string | null
  summary: string
  planContent: string | null
  actions: string[] | null
  recommendedAction: string | null
  completedEvent: ExitPlanModeCompletedEvent | null
}

export interface SubagentGroup {
  agentId: string
  agentName: string
  agentDisplayName: string
  description: string
  startEvent: CustomAgentStartedEvent
  endEvent: CustomAgentCompletedEvent | CustomAgentFailedEvent | null
  turns: Turn[]
  isPending: boolean
}

export interface SubagentGroupItem {
  kind: 'subagent_group'
  id: string
  timestamp: string
  group: SubagentGroup
}

export type TurnItem =
  | AssistantTextItem
  | ToolCallItem
  | AskUserItem
  | PermissionItem
  | ElicitationItem
  | PlanApprovalItem
  | SubagentGroupItem
  | RemoteSteerableItem

// ─── Turn ────────────────────────────────────────────────────────────
//
// A turn represents one user→assistant exchange cycle.
// The first turn may have no userMessage (session.start only).
// Items are in event arrival order within the turn.
//
// assistant.message with text AND toolRequests produces:
//   1. AssistantTextItem (text content, appended first)
//   2. ToolCallItem per toolRequest (appended in array order)
//
// tool.execution_complete mutates the existing ToolCallItem
// (sets completeEvent) — it does NOT create a new item.

export interface UserMessageItem {
  kind: 'user_message'
  id: string
  timestamp: string
  content: string
  source: string | undefined
  isPending: boolean
  isDismissed: boolean
}

export interface Turn {
  turnId: string | null
  userMessage: UserMessageItem | null
  items: TurnItem[]
  intent: string | null
  isComplete: boolean
}

// ─── Remote Steerable ────────────────────────────────────────────────

export interface RemoteSteerableItem {
  kind: 'remote_steerable'
  id: string
  timestamp: string
  enabled: boolean
}

export interface AbortItem {
  kind: 'abort'
  id: string
  timestamp: string
  reason: string
}

// ─── Top-Level Conversation Model ────────────────────────────────────
//
// The top-level items array interleaves turns with aborts.
// Render loop iterates this list:
//   - Turn → render userMessage bubble, then iterate turn.items
//   - AbortItem → render abort notice

export type ConversationItem = Turn | AbortItem

export interface ConversationModel {
  items: ConversationItem[]
  currentIntent: string | null
  isWorking: boolean
  sessionModel: string | null
  pendingPrompt: NormalizedPrompt | null
  allUnresolvedPrompts: NormalizedPrompt[]
  serverResolvedIds: ReadonlySet<string>
  /** Whether any item in the model produces visible rendered output. */
  hasVisibleContent: boolean
}

// ─── Type Guards ─────────────────────────────────────────────────────

export function isTurn(item: ConversationItem): item is Turn {
  return 'turnId' in item && 'items' in item
}

export function isAbortItem(item: ConversationItem): item is AbortItem {
  return 'kind' in item && item.kind === 'abort'
}
